import isValidEmail from 'validator/lib/isEmail';
import type { Nullish, UndecoratedProfile } from '@flemingmetrics/api-client';
import { CURRENCIES } from '@/constants/currencies';
import { Currency } from '@/types/currency';

type PriceOptions = { currency?: string; concise?: boolean; language?: string };

export function isStringEmpty(str: string, trim = true) {
  return trim ? str.trim().length === 0 : str.length === 0;
}

export function isBlank(str?: string | Nullish): boolean {
  return !str || str.trim().length === 0;
}

export function isString(val: any): val is string {
  return typeof val === 'string';
}

export function isEmail(email: string) {
  return isValidEmail(email.trim());
}

export function renderProfileName(
  profile?: Pick<UndecoratedProfile, 'firstname' | 'lastname'>,
  includeLastName = true,
  useLastInitial = false
) {
  if (!profile) {
    return '';
  } else if (!profile.lastname || !includeLastName) {
    return `${profile.firstname}`;
  } else if (useLastInitial) {
    return `${profile.firstname} ${profile.lastname.charAt(0)}.`;
  } else {
    return `${profile.firstname} ${profile.lastname}`;
  }
}

export function truncateString(str: string, length: number) {
  if (str.length < length) return str;
  return `${str.slice(0, length)}...`;
}

export function renderPrice(price = 0, options?: PriceOptions) {
  const { currency, concise, language } = parsePriceOptions(options);
  if (!currency || currency === '') {
    return concise ? formatConciseNumber(price) : formatNumber(price);
  }
  const formatter = new Intl.NumberFormat(language, {
    style: 'currency',
    currencyDisplay: 'narrowSymbol',
    currency,
  });
  return concise
    ? `${formatter.formatToParts(0).find((part) => part.type === 'currency')?.value}${formatConciseNumber(price)}`
    : formatter.format(price);
}

function parsePriceOptions(options: PriceOptions = {}) {
  return {
    currency: options.currency || 'EUR',
    concise: options.concise || false,
    language: options.language || navigator.language,
  };
}

export function formatCurrency(currencyValue: string | Nullish): string {
  if (isBlank(currencyValue)) return '';
  return CURRENCIES[currencyValue as Currency]?.symbol || '';
}

export function formatPrice(val: number, language = navigator.language) {
  return new Intl.NumberFormat(language, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(val);
}

export function formatNumber(
  val: number,
  fractionDigits: number | undefined = 2,
  language = navigator.language
) {
  return new Intl.NumberFormat(language, {
    maximumFractionDigits: fractionDigits,
  }).format(val);
}

export function formatPercentage(
  val: number,
  fractionDigits: number | undefined = 2,
  language = navigator.language
) {
  return new Intl.NumberFormat(language, {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(val);
}

function formatConciseNumber(val: number, language = navigator.language) {
  const value = Math.abs(val);
  const suffixes = ['', 'k', 'M', 'G', 'T'];
  const suffixIndex = value === 0 ? 0 : Math.floor(Math.log10(value) / 3);
  let shortValue = parseFloat(
    (suffixIndex !== 0 ? value / Math.pow(1000, suffixIndex) : value).toFixed(2)
  );
  if (shortValue % 1 !== 0) {
    shortValue = parseFloat(shortValue.toFixed(2));
  }
  return `${val < 0 ? '-' : ''}${formatNumber(shortValue, 2)}${suffixes[suffixIndex]}`;
}

export function strip(str: string, caseSensitive = false): string {
  return caseSensitive ? str.trim() : str.trim().toLowerCase();
}
